import { List, useTable } from '@refinedev/antd';
import { Table, Select, Space, Tag, Avatar, Badge } from 'antd';
import { FolderOutlined, UserOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { t } from '../../i18n';

interface DocumentUser {
  id: number;
  fullName: string;
  phone: string;
  photoUrl: string | null;
  totalDocuments: number;
  filteredDocuments: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
}

export const DocumentList = () => {
  const navigate = useNavigate();

  const { tableProps, setFilters } = useTable<DocumentUser>({
    resource: 'admin/documents/users',
    syncWithLocation: true,
    filters: {
      initial: [{ field: 'status', operator: 'eq', value: 'PENDING' }],
    },
  });

  const columns = [
    {
      title: '',
      key: 'icon',
      width: 48,
      render: () => (
        <FolderOutlined style={{ fontSize: 24, color: '#faad14' }} />
      ),
    },
    {
      title: t('support.ticketUser'),
      key: 'user',
      render: (_: unknown, record: DocumentUser) => (
        <Space>
          <Avatar
            src={record.photoUrl}
            icon={!record.photoUrl ? <UserOutlined /> : undefined}
            size={40}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.fullName || t('common.noName')}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('documents.title'),
      key: 'docCount',
      width: 140,
      render: (_: unknown, record: DocumentUser) => (
        <span>{record.filteredDocuments} ta</span>
      ),
    },
    {
      title: t('common.status'),
      key: 'status',
      width: 280,
      render: (_: unknown, record: DocumentUser) => (
        <Space>
          {record.pendingCount > 0 && (
            <Badge count={record.pendingCount} style={{ backgroundColor: '#faad14' }}>
              <Tag color="orange">{t('documents.pending')}</Tag>
            </Badge>
          )}
          {record.approvedCount > 0 && (
            <Badge count={record.approvedCount} style={{ backgroundColor: '#52c41a' }}>
              <Tag color="green">{t('documents.approved')}</Tag>
            </Badge>
          )}
          {record.rejectedCount > 0 && (
            <Badge count={record.rejectedCount} style={{ backgroundColor: '#ff4d4f' }}>
              <Tag color="red">{t('documents.rejected')}</Tag>
            </Badge>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 48,
      render: () => (
        <RightOutlined style={{ color: '#bfbfbf' }} />
      ),
    },
  ];

  return (
    <List
      title={t('documents.title')}
      headerButtons={
        <Select
          placeholder={t('common.status')}
          defaultValue="PENDING"
          style={{ width: 160 }}
          options={[
            { value: '', label: t('common.all') },
            { value: 'PENDING', label: t('documents.pending') },
            { value: 'APPROVED', label: t('documents.approved') },
            { value: 'REJECTED', label: t('documents.rejected') },
          ]}
          onChange={(value) => {
            setFilters([
              { field: 'status', operator: 'eq', value: value || undefined },
            ]);
          }}
        />
      }
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => navigate(`/documents/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </List>
  );
};
